import { DROPDOWN_VARIANTS, DROPDOWN_PARTS } from "../dropdownVocabulary";

// Style factories are plain CSS objects. Radii/padding/margin/gap are px strings: parts are also
// consumed through `sx`, where bare numbers become shape/spacing multipliers.
export const dropdownRecipe = {
  variants: {
    filterDropdown: {
      // filterDropDown-recipe.md: 36px = 8 + 18 content + 8 + 1px borders; #FDFDFD → paper (1.017:1).
      trigger: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius * 2}px`,
        padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
        minHeight: "36px",
        color: theme.palette.text.primary,
        fontSize: "0.875rem",
        lineHeight: "18px",
        cursor: "pointer",
        "&:hover": { borderColor: theme.palette.primary.interactive },
        "&.Mui-focused": { borderColor: theme.palette.primary.interactive },
        "&.Mui-error": { borderColor: theme.palette.formMessage.error },
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        "& .MuiInputBase-input, & .MuiSelect-select": {
          padding: 0,
          height: "18px",
          minHeight: "18px",
          lineHeight: "18px",
          fontSize: "0.875rem",
          "&::placeholder": { color: theme.palette.neutral.placeholder, opacity: 1 },
        },
        "& .MuiInputAdornment-root, & .MuiSelect-icon, & .MuiAutocomplete-endAdornment": {
          color: theme.palette.text.textGrey,
        },
        // MUI's own `.MuiSelect-select.MuiSelect-select` rule (minHeight 1.4375em, icon gutter) needs the same specificity.
        "& .MuiSelect-select.MuiSelect-select": { minHeight: "18px", height: "18px", paddingRight: 0 },
        "& .MuiSelect-icon": { position: "static", marginLeft: theme.spacing(1) },
        // Indicators sit in-flow (static) so the 18px content row stays 18: no MUI gutter, no 28px buttons.
        "&.MuiAutocomplete-inputRoot.MuiInputBase-root": { paddingRight: theme.spacing(1.5), flexWrap: "nowrap", alignItems: "center" },
        // In-flow adornment, self-centred so it does not depend on the root's alignment.
        "& .MuiAutocomplete-endAdornment, & .MuiAutocomplete-endAdornment.MuiAutocomplete-endAdornment": {
          position: "static",
          top: "auto",
          right: "auto",
          transform: "none",
          marginLeft: theme.spacing(1),
          display: "flex",
          alignItems: "center",
          alignSelf: "center",
          height: "18px",
        },
        "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
          padding: 0,
          margin: 0,
          height: "18px",
          width: "18px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          alignSelf: "center",
        },
        "& .MuiAutocomplete-clearIndicator .MuiSvgIcon-root": { fontSize: "16px" },
        "& .MuiChip-root": {
          height: "18px",
          fontSize: "0.75rem",
          margin: 0,
          marginRight: theme.spacing(0.5),
          "& .MuiChip-label": { paddingLeft: theme.spacing(1), paddingRight: theme.spacing(0.5) },
          "& .MuiChip-deleteIcon": { fontSize: "14px", margin: "0 2px 0 0" },
        },
      }),
      // filtermultiSelectDropDownOptions.md: container p16 r16 white + dropdownShadow; options 16 apart.
      panel: ({ theme }) => ({
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        border: "none",
        borderRadius: `${theme.shape.borderRadius * 4}px`,
        boxShadow: theme.palette.layout.dropdownShadow,
        // Scrolling list: the scrollbar sits in a reserved gutter inside the panel padding, 8px off the options.
        "& .MuiAutocomplete-listbox": {
          padding: `0 ${theme.spacing(1)} 0 0`,
          scrollbarGutter: "stable",
          scrollbarWidth: "thin",
          scrollbarColor: `${theme.palette.divider} transparent`,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: theme.palette.divider, borderRadius: "3px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        },
      }),
      // Option: r12, 1.5px border, p12; <tick> gap-1 <label 16px/700, lh 100%>; Figma #2F2F2F = formText.checkbox.
      option: ({ theme }) => ({
        margin: `0 0 ${theme.spacing(2)} 0`,
        padding: theme.spacing(1.5),
        border: `1.5px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius * 3}px`,
        color: theme.palette.formText.checkbox,
        fontSize: "1rem",
        fontWeight: theme.typography.fontWeightBold,
        lineHeight: 1,
        letterSpacing: 0,
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        "&:last-of-type": { marginBottom: 0 },
        "&:hover, &.Mui-focused, &.Mui-focusVisible": { backgroundColor: theme.palette.primary.softSurface },
      }),
      optionSelected: ({ theme }) => ({
        borderColor: theme.palette.primary.interactive,
        backgroundColor: theme.palette.primary.softSurface,
      }),
      // Tick: 24×24 = 2px padding + 20px icon, 1.5px border, rounded.
      tick: ({ theme }) => ({
        width: 24,
        height: 24,
        padding: "2px", // string: via sx a bare number here would be a spacing multiplier
        boxSizing: "border-box",
        flexShrink: 0,
        border: `1.5px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        backgroundColor: theme.palette.background.paper,
        color: "transparent",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        "& svg": { width: 20, height: 20 },
      }),
      tickSelected: ({ theme }) => ({
        borderColor: theme.palette.primary.interactive,
        backgroundColor: theme.palette.primary.interactive,
        color: theme.palette.primary.contrastText ?? theme.palette.common.white,
      }),
      // Just under the sticky nav (appBar) so a scrolled-up panel slides beneath it; never use inside a Dialog.
      popper: ({ theme }) => ({ zIndex: theme.zIndex.appBar - 1 }),
      // Range panel: 3px track (in-range interactive, rail unselectedBorder), 12px interactive thumbs.
      rangeSlider: ({ theme }) => ({
        color: theme.palette.primary.interactive,
        height: "3px",
        padding: `${theme.spacing(1.5)} 0`,
        "& .MuiSlider-track": { height: "3px", border: "none", backgroundColor: theme.palette.primary.interactive },
        "& .MuiSlider-rail": { height: "3px", opacity: 1, backgroundColor: theme.palette.optionCard.unselectedBorder },
        "& .MuiSlider-thumb": {
          width: "12px",
          height: "12px",
          backgroundColor: theme.palette.primary.interactive,
          "&::before": { boxShadow: "none" },
          "&:hover, &.Mui-focusVisible, &.Mui-active": { boxShadow: `0 0 0 6px ${theme.palette.primary.softSurface}` },
        },
      }),
      // Min/max boxes: r12, 1px iconMuted border, p5/10 (hug width); text 14px/500, lh 100%.
      // Figma's #9F9F9F text is 2.65:1 on white (fails AA) → textGrey (6.09 / 9.13).
      rangeValueBox: ({ theme }) => ({
        border: `1px solid ${theme.palette.neutral.iconMuted}`,
        borderRadius: `${theme.shape.borderRadius * 3}px`,
        padding: `${theme.spacing(0.625)} ${theme.spacing(1.25)}`,
        color: theme.palette.text.textGrey,
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        "&.Mui-focused": { borderColor: theme.palette.primary.interactive },
        "& .MuiInputBase-input": {
          padding: 0,
          height: "auto",
          fontSize: "0.875rem",
          fontWeight: 500,
          lineHeight: 1,
          verticalAlign: "middle",
        },
      }),
    },
  },
};

export function resolveDropdownPart(variant, part) {
  const parts = dropdownRecipe.variants[variant];
  if (!parts || !DROPDOWN_PARTS.includes(part)) {
    if (process.env.NODE_ENV !== "production" && variant != null) {
      throw new Error(`[dropdown-variants] unknown "${variant}.${part}" — add to dropdownVocabulary.js + recipes/dropdown.js first.`);
    }
    return null;
  }
  return parts[part];
}

// sx-ready: (theme) => style, or undefined when no variant is requested.
export const dropdownPartSx = (variant, part) => {
  const factory = variant ? resolveDropdownPart(variant, part) : null;
  return factory ? (theme) => factory({ theme }) : undefined;
};

export const DROPDOWN_VARIANT_LIST = DROPDOWN_VARIANTS;
